import React, { useState, useEffect, useContext } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Tooltip,
  Tabs,
  Tab,
  Paper,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Avatar
} from '@mui/material';
import {
  Add as AddIcon,
  Upload as UploadIcon,
  Download as DownloadIcon,
  Refresh as RefreshIcon,
  History as HistoryIcon,
  CheckCircle as CheckIcon,
  Warning as WarningIcon,
  Delete as DeleteIcon,
  CloudUpload as CloudUploadIcon
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import { useBrand } from '../../context/BrandContext';
import {
  createOpeningStock,
  getOpeningStockHistory,
  uploadOpeningStockCSV,
  getWarehouses,
  getInventoryBatches,
  formatDate
} from '../../services/inventoryService';

function TabPanel({ children, value, index }) {
  return (
    <div role="tabpanel" hidden={value !== index}>
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

function Adjustments() {
  const { user } = useAuth();
  const { selectedBrand } = useBrand();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  // Data
  const [warehouses, setWarehouses] = useState([]);
  const [history, setHistory] = useState([]);
  
  // Manual entry form
  const [manualDialogOpen, setManualDialogOpen] = useState(false);
  const [selectedWarehouse, setSelectedWarehouse] = useState('');
  const [entryDate, setEntryDate] = useState(new Date().toISOString().split('T')[0]);
  const [items, setItems] = useState([{
    sku: '',
    batch_number: '',
    quantity: '',
    unit_cost: '',
    expiry_date: '',
    notes: ''
  }]);

  // SKU → { product_name, image_url } cache. Populated on SKU blur by hitting
  // getInventoryBatches (which is already enriched from product_master). Same
  // pattern as PurchaseOrderEntry's fetchStockForSku.
  const [skuMeta, setSkuMeta] = useState({});
  const fetchSkuMeta = async (sku) => {
    if (!sku) return;
    const key = String(sku).toUpperCase();
    if (skuMeta[key] !== undefined) return;
    setSkuMeta(prev => ({ ...prev, [key]: { product_name: '', image_url: '' } })); // cache miss-sentinel
    try {
      const r = await getInventoryBatches({ sku: key, limit: 50 });
      const batches = r?.data?.batches || r?.batches || [];
      let image_url = '';
      let product_name = '';
      for (const b of batches) {
        if (!image_url && (b.thumbnail_url || b.image_url)) image_url = b.thumbnail_url || b.image_url;
        if (!product_name && b.product_name) product_name = b.product_name;
        if (image_url && product_name) break;
      }
      setSkuMeta(prev => ({ ...prev, [key]: { product_name, image_url } }));
    } catch (_) {
      // leave the sentinel — avoids re-fetching on every keystroke for a bad SKU
    }
  };

  // CSV upload
  const [csvDialogOpen, setCsvDialogOpen] = useState(false);
  const [csvFile, setCsvFile] = useState(null);
  const [csvUploadResult, setCsvUploadResult] = useState(null);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    loadData();
  }, [user?.company_id, selectedBrand?.id]);

  const loadData = async () => {
    if (!user?.company_id || !selectedBrand?.id) {
      return;
    }

    setLoading(true);
    setError(null);
    
    try {
      const [warehousesRes, historyRes] = await Promise.all([
        getWarehouses({ is_active: true }),
        getOpeningStockHistory({ 
          company_id: user.company_id,
          brand_id: selectedBrand.id
        })
      ]);
      
      setWarehouses(warehousesRes?.data || []);
      setHistory(historyRes?.opening_stock_entries || []);
    } catch (err) {
      console.error('Failed to load data:', err);
      setError('Failed to load data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Manual entry functions
  const addItemRow = () => {
    setItems([...items, {
      sku: '',
      batch_number: '',
      quantity: '',
      unit_cost: '',
      expiry_date: '',
      notes: ''
    }]);
  };

  const removeItemRow = (index) => {
    setItems(items.filter((_, i) => i !== index));
  };

  const updateItem = (index, field, value) => {
    const updated = [...items];
    updated[index][field] = value;
    setItems(updated);
  };

  const handleManualSubmit = async () => {
    setError(null);
    setSuccess(null);

    // Validation
    if (!selectedWarehouse) {
      setError('Please select a warehouse');
      return;
    }

    if (!entryDate) {
      setError('Please select an entry date');
      return;
    }

    // Validate items
    const validItems = items.filter(item => 
      item.sku && item.batch_number && item.quantity && item.unit_cost
    );

    if (validItems.length === 0) {
      setError('Please add at least one valid item (SKU, Batch #, Quantity, and Unit Cost are required)');
      return;
    }

    setLoading(true);
    
    try {
      const result = await createOpeningStock({
        company_id: user.company_id,
        brand_id: selectedBrand.id,
        warehouse_id: parseInt(selectedWarehouse),
        entry_date: entryDate,
        items: validItems.map(item => ({
          ...item,
          quantity: parseFloat(item.quantity),
          unit_cost: parseFloat(item.unit_cost)
        }))
      });

      setSuccess(`Successfully created opening stock for ${result.batches_created} items`);
      setManualDialogOpen(false);
      
      // Reset form
      setSelectedWarehouse('');
      setEntryDate(new Date().toISOString().split('T')[0]);
      setItems([{
        sku: '',
        batch_number: '',
        quantity: '',
        unit_cost: '',
        expiry_date: '',
        notes: ''
      }]);
      
      // Reload data
      await loadData();
    } catch (err) {
      console.error('Failed to create opening stock:', err);
      setError(err.response?.data?.error || 'Failed to create opening stock');
    } finally {
      setLoading(false);
    }
  };

  // CSV upload functions
  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (!file.name.endsWith('.csv')) {
        setError('Please select a CSV file');
        return;
      }
      setCsvFile(file);
      setError(null);
    }
  };

  const downloadCSVTemplate = () => {
    const template = `sku,batch_number,quantity,unit_cost,expiry_date,warehouse_code,notes
SKU-001,BATCH-001,100,12.50,2025-12-31,WH-MAIN,Opening stock
SKU-002,BATCH-002,50,8.75,,WH-MAIN,No expiry date
SKU-003,BATCH-003,200,5.00,2025-06-30,WH-MAIN,Perishable item`;

    const blob = new Blob([template], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'opening_stock_template.csv';
    link.click();
  };

  const handleCSVUpload = async () => {
    if (!csvFile) {
      setError('Please select a file');
      return;
    }

    if (!entryDate) {
      setError('Please select an entry date');
      return;
    }

    setUploading(true);
    setError(null);
    setCsvUploadResult(null);

    try {
      const result = await uploadOpeningStockCSV(
        csvFile,
        user.company_id,
        selectedBrand.id,
        entryDate
      );

      setCsvUploadResult(result);
      setSuccess(result.message || 'CSV uploaded successfully');
      
      // Reload data after successful upload
      await loadData();
    } catch (err) {
      console.error('Failed to upload CSV:', err);
      const errorData = err.response?.data;
      
      if (errorData?.errors) {
        setError(
          <Box>
            <Typography variant="body2" gutterBottom>CSV validation failed:</Typography>
            <List dense>
              {errorData.errors.map((error, idx) => (
                <ListItem key={idx} sx={{ py: 0 }}>
                  <ListItemIcon sx={{ minWidth: 30 }}>
                    <WarningIcon color="error" fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary={error} />
                </ListItem>
              ))}
            </List>
          </Box>
        );
      } else {
        setError(errorData?.error || 'Failed to upload CSV');
      }
    } finally {
      setUploading(false);
    }
  };

  const resetCSVUpload = () => {
    setCsvFile(null);
    setCsvUploadResult(null);
    setError(null);
    const fileInput = document.getElementById('csv-file-input');
    if (fileInput) fileInput.value = '';
  };

  if (!user?.company_id || !selectedBrand?.id) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">
          Please select a company and brand to manage inventory adjustments.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h4" gutterBottom>
            Inventory Adjustments
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Create opening stock and view adjustment history
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={loadData}
          disabled={loading}
        >
          Refresh
        </Button>
      </Box>

      {/* Alerts */}
      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" onClose={() => setSuccess(null)} sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      {/* Action Buttons */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <AddIcon sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6">Manual Entry</Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Enter opening stock manually for one or more items
              </Typography>
              <Button
                variant="contained"
                fullWidth
                startIcon={<AddIcon />}
                onClick={() => { setError(null); setManualDialogOpen(true); }}
              >
                Create Manual Entry
              </Button>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <CloudUploadIcon sx={{ mr: 1, color: 'secondary.main' }} />
                <Typography variant="h6">Bulk Upload</Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Upload opening stock from a CSV file
              </Typography>
              <Button
                variant="contained"
                color="secondary"
                fullWidth
                startIcon={<UploadIcon />}
                onClick={() => { setError(null); setCsvDialogOpen(true); }}
              >
                Upload CSV File
              </Button>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* History Table */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Opening Stock History
          </Typography>
          
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : history.length === 0 ? (
            <Alert severity="info">
              No opening stock entries found. Create your first entry above.
            </Alert>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell>SKU</TableCell>
                    <TableCell>Batch Number</TableCell>
                    <TableCell>Warehouse</TableCell>
                    <TableCell align="right">Quantity</TableCell>
                    <TableCell>Reason</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {history.map((entry) => (
                    <TableRow key={entry.id}>
                      <TableCell>{formatDate(entry.entry_date)}</TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          {entry.sku}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip label={entry.batch_number} size="small" />
                      </TableCell>
                      <TableCell>{entry.warehouse_code}</TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight="medium">
                          {entry.quantity}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary" noWrap>
                          {entry.reason}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Manual Entry Dialog */}
      <Dialog 
        open={manualDialogOpen} 
        onClose={() => setManualDialogOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>Create Opening Stock - Manual Entry</DialogTitle>
        <DialogContent>
          {/* DT-1922: this dialog stays open when its action fails; the page Alert sits behind the modal. */}
          {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Warehouse *</InputLabel>
                <Select
                  value={selectedWarehouse}
                  onChange={(e) => setSelectedWarehouse(e.target.value)}
                  label="Warehouse *"
                >
                  <MenuItem value="">Select Warehouse</MenuItem>
                  {warehouses.map((wh) => (
                    <MenuItem key={wh.id} value={wh.id}>
                      {wh.warehouse_code} - {wh.warehouse_name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Entry Date *"
                type="date"
                value={entryDate}
                onChange={(e) => setEntryDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                inputProps={{ min: '2000-01-01' }}
              />
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">Items</Typography>
                <Button
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={addItemRow}
                >
                  Add Item
                </Button>
              </Box>
            </Grid>

            {items.map((item, index) => {
              const meta = skuMeta[String(item.sku || '').toUpperCase()] || {};
              return (
              <Grid item xs={12} key={index}>
                <Paper sx={{ p: 2 }} variant="outlined">
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs="auto">
                      <Tooltip title={meta.product_name || ''} placement="top">
                        <Avatar
                          src={meta.image_url || undefined}
                          variant="rounded"
                          sx={{
                            width: 44, height: 44,
                            bgcolor: meta.image_url ? 'transparent' : 'grey.100',
                            color: 'text.secondary',
                            fontSize: 10,
                          }}
                        >
                          {meta.image_url ? null : 'IMG'}
                        </Avatar>
                      </Tooltip>
                    </Grid>
                    <Grid item xs={12} md={2}>
                      <TextField
                        fullWidth
                        label="SKU *"
                        size="small"
                        value={item.sku}
                        onChange={(e) => updateItem(index, 'sku', e.target.value)}
                        onBlur={(e) => fetchSkuMeta(e.target.value)}
                        helperText={meta.product_name ? meta.product_name.slice(0, 40) : ''}
                        FormHelperTextProps={{ sx: { fontSize: 10, mx: 0.5 } }}
                      />
                    </Grid>
                    <Grid item xs={12} md={2}>
                      <TextField
                        fullWidth
                        label="Batch Number *"
                        size="small"
                        value={item.batch_number}
                        onChange={(e) => updateItem(index, 'batch_number', e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12} md={2}>
                      <TextField
                        fullWidth
                        label="Quantity *"
                        size="small"
                        type="number"
                        value={item.quantity}
                        onChange={(e) => updateItem(index, 'quantity', e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12} md={2}>
                      <TextField
                        fullWidth
                        label="Unit Cost *"
                        size="small"
                        type="number"
                        value={item.unit_cost}
                        onChange={(e) => updateItem(index, 'unit_cost', e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12} md={2}>
                      <TextField
                        fullWidth
                        label="Expiry Date"
                        size="small"
                        type="date"
                        value={item.expiry_date}
                        onChange={(e) => updateItem(index, 'expiry_date', e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        inputProps={{ min: '2000-01-01' }}
                      />
                    </Grid>
                    <Grid item xs={12} md={1}>
                      <Tooltip title="Remove item">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => removeItemRow(index)}
                          disabled={items.length === 1}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Notes"
                        size="small"
                        multiline
                        rows={1}
                        value={item.notes}
                        onChange={(e) => updateItem(index, 'notes', e.target.value)}
                      />
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>
            );
            })}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setManualDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleManualSubmit}
            disabled={loading}
            startIcon={loading ? <CircularProgress size={16} /> : <CheckIcon />}
          >
            Create Opening Stock
          </Button>
        </DialogActions>
      </Dialog>

      {/* CSV Upload Dialog */}
      <Dialog 
        open={csvDialogOpen} 
        onClose={() => !uploading && setCsvDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Upload Opening Stock from CSV</DialogTitle>
        <DialogContent>
          {/* DT-1922: this dialog stays open when its action fails; the page Alert sits behind the modal. */}
          {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
          <Box sx={{ mt: 2 }}>
            <Alert severity="info" sx={{ mb: 3 }}>
              <Typography variant="body2" gutterBottom>
                CSV file must include these columns:
              </Typography>
              <Typography variant="body2" component="div">
                <strong>Required:</strong> sku, batch_number, quantity, unit_cost, warehouse_code
                <br />
                <strong>Optional:</strong> expiry_date, notes
              </Typography>
            </Alert>

            <Button
              startIcon={<DownloadIcon />}
              onClick={downloadCSVTemplate}
              sx={{ mb: 3 }}
            >
              Download CSV Template
            </Button>

            <TextField
              fullWidth
              label="Entry Date *"
              type="date"
              value={entryDate}
              onChange={(e) => setEntryDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ min: '2000-01-01' }}
              sx={{ mb: 3 }}
            />

            <Box
              sx={{
                border: '2px dashed',
                borderColor: 'divider',
                borderRadius: 1,
                p: 4,
                textAlign: 'center',
                mb: 2
              }}
            >
              <input
                id="csv-file-input"
                type="file"
                accept=".csv"
                style={{ display: 'none' }}
                onChange={handleFileSelect}
              />
              <label htmlFor="csv-file-input">
                <Button
                  variant="outlined"
                  component="span"
                  startIcon={<CloudUploadIcon />}
                >
                  Select CSV File
                </Button>
              </label>
              {csvFile && (
                <Box sx={{ mt: 2 }}>
                  <Chip
                    label={csvFile.name}
                    onDelete={resetCSVUpload}
                    color="primary"
                  />
                </Box>
              )}
            </Box>

            {csvUploadResult && (
              <Alert severity="success" sx={{ mb: 2 }}>
                <Typography variant="body2" gutterBottom>
                  <strong>{csvUploadResult.message}</strong>
                </Typography>
                <Typography variant="body2">
                  Total rows: {csvUploadResult.summary?.total_rows} | 
                  Processed: {csvUploadResult.summary?.items_processed} | 
                  Batches created: {csvUploadResult.summary?.batches_created}
                </Typography>
                {csvUploadResult.warnings && csvUploadResult.warnings.length > 0 && (
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="body2" fontWeight="bold">Warnings:</Typography>
                    {csvUploadResult.warnings.map((warning, idx) => (
                      <Typography key={idx} variant="body2" color="warning.main">
                        • {warning}
                      </Typography>
                    ))}
                  </Box>
                )}
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCsvDialogOpen(false)} disabled={uploading}>
            Close
          </Button>
          <Button
            variant="contained"
            onClick={handleCSVUpload}
            disabled={!csvFile || uploading}
            startIcon={uploading ? <CircularProgress size={16} /> : <UploadIcon />}
          >
            {uploading ? 'Uploading...' : 'Upload CSV'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default Adjustments;
